<template>
  <div class="budget-table">
    <el-descriptions 
      :column="2" 
      border
      class="budget-overview"
    >
      <el-descriptions-item label="💰 预算总额" :span="2">
        <span class="total-budget">¥{{ budget.total?.toLocaleString() }}</span>
      </el-descriptions-item>
    </el-descriptions>

    <el-table :data="budgetItems" stripe style="width: 100%" class="budget-detail-table">
      <el-table-column prop="category" label="类别" width="120">
        <template #default="{ row }">
          <div class="category-cell">
            <span class="category-icon">{{ row.icon }}</span>
            <span>{{ row.category }}</span>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="description" label="说明" min-width="200" />

      <el-table-column prop="amount" label="金额" width="120" align="right">
        <template #default="{ row }">
          <span :class="{ 'highlight-amount': row.isTotal }">¥{{ row.amount?.toLocaleString() }}</span>
        </template>
      </el-table-column>

      <el-table-column label="占比" width="100" align="center">
        <template #default="{ row }">
          <el-progress 
            v-if="!row.isTotal && budget.total > 0"
            :percentage="Math.round((row.amount / budget.total) * 100)" 
            :color="row.color"
            :stroke-width="8"
            :show-text="false"
          />
        </template>
      </el-table-column>
    </el-table>

    <!-- 预算分析 -->
    <el-alert
      v-if="budgetAnalysis"
      :title="budgetAnalysis.title"
      :type="budgetAnalysis.type"
      :description="budgetAnalysis.description"
      show-icon
      class="budget-analysis"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  budget: {
    type: Object,
    required: true
  }
})

const budgetItems = computed(() => {
  if (!props.budget) return []

  const items = [
    {
      category: '交通',
      icon: '🚗',
      description: `油费¥${props.budget.fuel} + 高速费¥${props.budget.toll}`,
      amount: (props.budget.fuel || 0) + (props.budget.toll || 0),
      color: '#00B5FF'
    },
    {
      category: '门票',
      icon: '🎫',
      description: '景点门票费用',
      amount: props.budget.tickets || 0,
      color: '#67C23A'
    },
    {
      category: '住宿',
      icon: '🏨',
      description: '酒店/民宿费用',
      amount: props.budget.hotel || 0,
      color: '#E6A23C'
    },
    {
      category: '餐饮',
      icon: '🍽️',
      description: '每日三餐费用',
      amount: props.budget.food || 0,
      color: '#F56C6C'
    },
    {
      category: '其他',
      icon: '🛍️',
      description: '购物及杂费',
      amount: props.budget.other || 0,
      color: '#909399'
    }
  ]

  // Add total row
  items.push({
    category: '总计',
    icon: '💰',
    description: '全部费用合计',
    amount: props.budget.total || 0,
    isTotal: true,
    color: '#00B5FF'
  })

  return items
})

const budgetAnalysis = computed(() => {
  if (!props.budget || !props.budget.total) return null

  const total = props.budget.total
  
  if (total < 2000) {
    return {
      title: '经济实惠',
      type: 'success',
      description: '这是一条性价比很高的路线，适合预算有限的旅行者。'
    }
  } else if (total < 5000) {
    return {
      title: '标准消费',
      type: 'info',
      description: '这是一条中等预算的路线，适合大多数旅行者。'
    }
  } else if (total < 10000) {
    return {
      title: '品质之选',
      type: 'warning',
      description: '这是一条高品质路线，包含较好的住宿和餐饮体验。'
    }
  } else {
    return {
      title: '奢华体验',
      type: 'danger',
      description: '这是一条高端路线，提供顶级的旅行体验。'
    }
  }
})
</script>

<style scoped>
.budget-overview {
  margin-bottom: 20px;
}

.total-budget {
  font-size: 32px;
  font-weight: bold;
  background: linear-gradient(135deg, #00B5FF 0%, #0077FF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.category-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.category-icon {
  font-size: 20px;
}

.highlight-amount {
  font-size: 16px;
  font-weight: bold;
  color: #FF6B35;
}

.budget-detail-table :deep(.el-table__row:last-child) {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  font-weight: bold;
}

.budget-detail-table :deep(.el-table__row:last-child td) {
  border-top: 2px solid #00B5FF;
}

.budget-analysis {
  margin-top: 20px;
}
</style>